import { RoleModel } from '@/http/models/Role'
import { toast } from 'sonner'

import { useState } from 'react'

import { RoleModel as RoleType } from '@/types'

import { useFetchRoles } from './useFetchRoles'

type DialogMode = 'create' | 'edit' | 'view'

export const useRolesTable = () => {
  const [mode, setMode] = useState<DialogMode>('create')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editRole, setEditRole] = useState<RoleType.Role | null>(null)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [roleToDelete, setRoleToDelete] = useState<RoleType.Role | null>(null)

  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { data, loading, pagination, setPage, setPageSize, refresh } = useFetchRoles()

  const openCreateDialog = () => {
    setMode('create')
    setEditRole(null)
    setDialogOpen(true)
  }

  const openViewDialog = (role: RoleType.Role) => {
    setMode('view')
    setEditRole(role)
    setDialogOpen(true)
  }

  const openEditDialog = async (role: RoleType.Role) => {
    setMode('edit')
    setUpdateLoading(true)
    // Fetch full role details with permissions
    const response = await RoleModel.getById({ params: { id: role._id } })
    if (response.success && response.data) {
      setEditRole(response.data.result)
      setDialogOpen(true)
    } else {
      toast.error('Failed to fetch role details')
    }
    setUpdateLoading(false)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setEditRole(null)
  }

  const handleDeleteClick = (role: RoleType.Role) => {
    setRoleToDelete(role)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!roleToDelete?._id) return

    setDeleteLoading(true)
    const response = await RoleModel.delete({
      params: { id: roleToDelete._id },
    })

    if (response.success) {
      toast.success('Role deleted successfully')
      setDeleteDialogOpen(false)
      setRoleToDelete(null)
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to delete role')
    }
    setDeleteLoading(false)
  }

  const handleCreateRole = async (data: any) => {
    setCreateLoading(true)
    const response = await RoleModel.create({
      body: data,
    })

    if (response.success) {
      toast.success('Role created successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to create role')
    }
    setCreateLoading(false)
  }

  const handleUpdateRole = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await RoleModel.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('Role updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to update role')
    }
    setUpdateLoading(false)
  }

  return {
    loading,
    data: data as RoleType.Role[],
    pagination,
    mode,
    dialogOpen,
    editRole,
    deleteDialogOpen,
    roleToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    refresh,
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    handleCreateRole,
    handleUpdateRole,
  }
}
